import { skipToken } from "@reduxjs/toolkit/query";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import { useState, type FC } from "react";
import {
  useAiUsageQuery,
  useVoiceUsageQuery,
} from "../../../api/entities/company.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectCompany } from "../../company/companySlice.js";
import { SettingsSection } from "../shared/settingsSection.js";
import { AiCreditsSection } from "./aiCreditsSection.js";
import { CreditUsageHeader } from "./creditUsageSection.js";
import { PlanSection } from "./planSection.js";
import { VoiceCreditsSection } from "./voiceCreditsSection.js";

/**
 * The redesigned billing page (plan summary + AI usage), shown when the
 * `new-billing` flag is on — see {@link BillingSettingsGate}.
 */
export const BillingSettingsPage: FC = () => {
  // The voice usage breakdown only applies to companies with the Voice agents
  // workspace.
  const hasVoiceAgents = useIsFeatureFlagEnabled(FeatureFlagKey.AgentsTab);
  const company = useAppSelector(selectCompany);
  const [periodOffset, setPeriodOffset] = useState(0);
  const hasBilling = Boolean(company?.billing_company_id);

  // Both reads resolve the same customer, contract and statement, so they
  // fail together; the page answers for the whole block rather than each
  // section announcing the same failure. Deduped with the sections' own
  // queries, and with the plan summary's current-period read.
  const currentAiUsage = useAiUsageQuery(
    hasBilling ? { periodOffset: 0 } : skipToken,
  );
  const aiUsage = useAiUsageQuery(hasBilling ? { periodOffset } : skipToken);
  const voiceUsage = useVoiceUsageQuery(
    hasBilling && hasVoiceAgents ? { periodOffset } : skipToken,
  );
  const isUsageError = aiUsage.isError || voiceUsage.isError;
  const hasReadableUsage = hasBilling && !isUsageError;

  return (
    <SettingsSection>
      <div className="space-y-6">
        <header>
          <h1 className="text-xl leading-6 font-semibold text-foreground">
            Billing
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your plan, invoices, and AI usage.
          </p>
        </header>

        <PlanSection />

        <div className="space-y-3">
          <CreditUsageHeader
            onPeriodOffsetChange={setPeriodOffset}
            periodOffset={periodOffset}
            periods={
              hasReadableUsage ? currentAiUsage.currentData?.periods : undefined
            }
          />
          <UsageBlockBanner
            hasBilling={hasBilling}
            isUsageError={isUsageError}
          />
        </div>
        {hasReadableUsage ? (
          <AiCreditsSection periodOffset={periodOffset} />
        ) : null}
        {hasReadableUsage && hasVoiceAgents ? (
          <VoiceCreditsSection periodOffset={periodOffset} />
        ) : null}
      </div>
    </SettingsSection>
  );
};

interface UsageBlockBannerProps {
  hasBilling: boolean;
  isUsageError: boolean;
}

/** What stands in for the usage sections, or the preview notice above them. */
const UsageBlockBanner: FC<UsageBlockBannerProps> = (props) => {
  if (!props.hasBilling) {
    return (
      <div role="status">
        <InfoBanner title="Usage and credits appear here once billing is set up for this workspace" />
      </div>
    );
  }
  if (props.isUsageError) {
    return (
      <div role="alert">
        <InfoBanner
          title="Unable to load usage and credits"
          description="Refresh the page to try again."
          variant={SimpleModalVariant.Warning}
        />
      </div>
    );
  }
  return (
    <InfoBanner
      title="Usage preview"
      description="Usage is tracked, but nothing is billed or paused based on it."
    />
  );
};
